import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Modal, Badge, Tabs, Tab, Alert, Spinner } from 'react-bootstrap';
import { FaEnvelope, FaUsers, FaHistory, FaEye, FaPaperPlane } from 'react-icons/fa';
import { usersService } from '../../services/usersService';
import { emailService } from '../../services/emailService';
import { showToast, getErrorMessage } from '../../utils/toast';

const NotificationsTab = () => {
  const [activeTab, setActiveTab] = useState('compose');
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchEmailLogs();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await usersService.getUsers();
      setUsers(response.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      showToast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailLogs = async () => {
    try {
      const response = await emailService.getEmailLogs();
      setEmailLogs(response.logs || []);
    } catch (error) {
      console.error('Error fetching email logs:', error);
      showToast.error(getErrorMessage(error));
    }
  };

  const handleUserSelect = (userId, isSelected) => {
    if (isSelected) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedUsers(users.map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim()) {
      showToast.warning('Please enter an email subject');
      return;
    }
    if (!emailContent.trim()) {
      showToast.warning('Please enter email content');
      return;
    }
    if (selectedUsers.length === 0) {
      showToast.warning('Please select at least one recipient');
      return;
    }

    try {
      setSendingEmail(true);

      // Send email via API
      const response = await emailService.sendBulkEmail({
        subject: emailSubject,
        content: emailContent,
        userIds: selectedUsers
      });

      // Reset form
      setEmailSubject('');
      setEmailContent('');
      setSelectedUsers([]);

      // Refresh email logs
      await fetchEmailLogs();

      if (response.status === 'Processing') {
        showToast.success(`Email processing started for ${response.totalRecipients} recipients! Check Email Logs for delivery status.`);
      } else {
        showToast.success(`Email sent successfully! ${response.successCount} of ${response.totalRecipients} emails delivered.`);
        
        if (response.failedCount > 0) {
          showToast.warning(`${response.failedCount} emails failed to send.`);
        }
      }

    } catch (error) {
      console.error('Error sending email:', error);
      showToast.error(getErrorMessage(error));
    } finally {
      setSendingEmail(false);
    }
  };

  const handlePreviewEmail = (log) => {
    setPreviewData(log);
    setShowPreviewModal(true);
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Sent': 'success',
      'Failed': 'danger',
      'Pending': 'warning',
      'Processing': 'info',
      'Sending': 'warning',
      'Partial': 'warning'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const selectedUserNames = users
    .filter(user => selectedUsers.includes(user.id))
    .map(user => `${user.first_name} ${user.sur_name}`)
    .join(', ');

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <h2 className="mb-3">
            <FaEnvelope className="me-2" />
            Email Management System
          </h2>
          <p className="text-muted">Compose and send emails to users, and track email delivery status.</p>
        </Col>
      </Row>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
        {/* Compose Email Tab */}
        <Tab eventKey="compose" title={<><FaEnvelope className="me-2" />Compose Email</>}>
          <Row>
            <Col lg={8}>
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">
                    <FaPaperPlane className="me-2" />
                    Compose New Email
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>Email Subject *</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter email subject..."
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        disabled={sendingEmail}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Email Content *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={10}
                        placeholder="Enter your email content here..."
                        value={emailContent}
                        onChange={(e) => setEmailContent(e.target.value)}
                        disabled={sendingEmail}
                        style={{ minHeight: '250px' }}
                      />
                      <Form.Text className="text-muted">
                        You can use HTML tags for formatting. Use <code>{"{name}"}</code> to personalize emails with user names.
                      </Form.Text>
                    </Form.Group>

                    {selectedUsers.length > 0 && (
                      <Alert variant="info">
                        <strong>Selected Recipients ({selectedUsers.length}):</strong><br />
                        <small>{selectedUserNames}</small>
                      </Alert>
                    )}

                    <div className="d-flex justify-content-between align-items-center">
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={handleSendEmail}
                        disabled={sendingEmail || selectedUsers.length === 0 || !emailSubject.trim() || !emailContent.trim()}
                      >
                        {sendingEmail ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <FaPaperPlane className="me-2" />
                            Send Email ({selectedUsers.length} recipients)
                          </>
                        )}
                      </Button>
                      
                      <div className="text-muted">
                        <small>* Required fields</small>
                      </div>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                      <FaUsers className="me-2" />
                      Select Recipients
                    </h6>
                    <Form.Check
                      type="checkbox"
                      label="Select All"
                      checked={selectedUsers.length === users.length && users.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      disabled={sendingEmail || loading}
                    />
                  </div>
                </Card.Header>
                <Card.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  {loading ? (
                    <div className="text-center p-3">
                      <Spinner animation="border" />
                      <p className="mt-2 text-muted">Loading users...</p>
                    </div>
                  ) : users.length === 0 ? (
                    <p className="text-muted text-center">No users found</p>
                  ) : (
                    <div>
                      {users.map((user) => (
                        <div key={user.id} className="d-flex align-items-center mb-2 p-2 border rounded">
                          <Form.Check
                            type="checkbox"
                            checked={selectedUsers.includes(user.id)}
                            onChange={(e) => handleUserSelect(user.id, e.target.checked)}
                            disabled={sendingEmail}
                            className="me-2"
                          />
                          <div className="flex-grow-1">
                            <div className="fw-bold">{user.first_name} {user.sur_name}</div>
                            <small className="text-muted">{user.email}</small>
                            <br />
                            <Badge bg={user.is_account_active ? 'success' : 'secondary'} className="me-1">
                              {user.is_account_active ? 'Active' : 'Inactive'}
                            </Badge>
                            <Badge bg="info">{user.subscription_type}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card.Body>
                <Card.Footer>
                  <small className="text-muted">
                    {selectedUsers.length} of {users.length} users selected
                  </small>
                </Card.Footer>
              </Card>
            </Col>
          </Row>
        </Tab>

        {/* Email Logs Tab */}
        <Tab eventKey="logs" title={<><FaHistory className="me-2" />Email Logs</>}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <FaHistory className="me-2" />
                Email Delivery Logs ({emailLogs.length})
              </h5>
            </Card.Header>
            <Card.Body>
              {emailLogs.length === 0 ? (
                <div className="text-center p-4">
                  <FaEnvelope size={48} className="text-muted mb-3" />
                  <p className="text-muted">No emails sent yet</p>
                </div>
              ) : (
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Subject</th>
                      <th>Recipients</th>
                      <th>Status</th>
                      <th>Sent Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailLogs.map((log) => (
                      <tr key={log.id}>
                        <td>
                          <strong>{log.subject}</strong>
                          <br />
                          <small className="text-muted">
                            {log.content.substring(0, 50)}...
                          </small>
                        </td>
                        <td>
                          <Badge bg="primary" className="me-1">
                            {log.recipient_count} users
                          </Badge>
                          <br />
                          <small className="text-muted">
                            {log.recipient_emails && Array.isArray(log.recipient_emails) ? (
                              <>
                                {log.recipient_emails.slice(0, 2).join(', ')}
                                {log.recipient_emails.length > 2 && ` +${log.recipient_emails.length - 2} more`}
                              </>
                            ) : (
                              'No recipients'
                            )}
                          </small>
                        </td>
                        <td>{getStatusBadge(log.status)}</td>
                        <td>
                          <small>{log.sent_at || log.created_at}</small>
                        </td>
                        <td>
                          <Button
                            variant="outline-info"
                            size="sm"
                            className="me-1"
                            onClick={() => handlePreviewEmail(log)}
                          >
                            <FaEye />
                          </Button>
                          {log.status === 'Failed' && (
                            <Button
                              variant="outline-warning"
                              size="sm"
                              title="Retry sending"
                            >
                              <FaPaperPlane />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Email Preview Modal */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Email Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewData && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Subject:</strong>
                  <p>{previewData.subject}</p>
                </Col>
                <Col md={6}>
                  <strong>Status:</strong>
                  <p>{getStatusBadge(previewData.status)}</p>
                </Col>
              </Row>
              
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Recipients:</strong>
                  <p>{previewData.recipient_count} users</p>
                </Col>
                <Col md={6}>
                  <strong>Sent Date:</strong>
                  <p>{previewData.sent_at || previewData.created_at}</p>
                </Col>
              </Row>

              {previewData.success_count !== undefined && (
                <Row className="mb-3">
                  <Col md={6}>
                    <strong>Success Count:</strong>
                    <p className="text-success">{previewData.success_count}</p>
                  </Col>
                  <Col md={6}>
                    <strong>Failed Count:</strong>
                    <p className="text-danger">{previewData.failed_count || 0}</p>
                  </Col>
                </Row>
              )}

              <div className="mb-3">
                <strong>Recipient Emails:</strong>
                <div className="mt-2">
                  {previewData.recipient_emails && Array.isArray(previewData.recipient_emails) ? 
                    previewData.recipient_emails.map((email, index) => (
                      <Badge key={index} bg="secondary" className="me-1 mb-1">
                        {email}
                      </Badge>
                    )) : (
                      <p className="text-muted">No recipient emails available</p>
                    )
                  }
                </div>
              </div>

              {previewData.failed_emails && previewData.failed_emails.length > 0 && (
                <div className="mb-3">
                  <strong>Failed Emails:</strong>
                  <div className="mt-2">
                    {previewData.failed_emails.map((email, index) => (
                      <Badge key={index} bg="danger" className="me-1 mb-1">
                        {email}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-3">
                <strong>Email Content:</strong>
                <div className="border p-3 mt-2" style={{ backgroundColor: '#f8f9fa' }}>
                  <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                    {previewData.content}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default NotificationsTab;